import { registerPlugin } from '@capacitor/core';
import { INDICATOR_DEFAULTS, type Indicators } from './indicators.ts';

/**
 * Carries the golfer's settings from the phone to the CAR SCREEN.
 *
 * 🟥 THE PROBLEM THIS SOLVES. The phone app is Capacitor-bundled and runs on
 * `capacitor://localhost`. CarPlay loads the hosted app over https. Different
 * origins, so they share no localStorage — an indicator switched off on the
 * phone reached the car screen through nothing at all, and the car quietly kept
 * its own defaults. To a golfer that is indistinguishable from a broken toggle.
 *
 * The App Group `group.com.gocaddy.golf` carries it: the same shared container
 * the yardage widget and the Watch already read, so the entitlement and the
 * protection class are proven rather than hoped for. NOT the fleet socket —
 * that is a Durable Object for broadcasting a course's buggies to a pro shop,
 * and this product's customers own one cart each.
 */

interface SettingsBridgePlugin {
  setSettings(o: { json: string }): Promise<void>;
}
const Native = registerPlugin<SettingsBridgePlugin>('YardageWidget');

/** The subset the car screen actually renders with. */
export interface CarSettings {
  indicators: Indicators;
  mapMode: string;
  teeColour: string;
  unit: 'm' | 'yd';
  lockToHole: boolean;
  courseId?: string | number | null;
}

/** Phone side: publish. Silent when not running natively — the web build has
 *  no App Group and failing here would spam a console for no benefit. */
export async function publishCarSettings(s: CarSettings): Promise<void> {
  try {
    await Native.setSettings({ json: JSON.stringify(s) });
  } catch {
    /* not native, or App Group unavailable — see the plugin, which rejects loudly */
  }
}

/** Car side: receive. CarPlaySceneDelegate calls this after each page load. */
export function acceptCarSettings(onApply: (s: CarSettings) => void): () => void {
  const w = window as unknown as { __gocaddyApplySettings?: (json: string) => void };
  w.__gocaddyApplySettings = (json: string) => {
    try {
      const parsed = JSON.parse(json) as Partial<CarSettings>;
      onApply({
        // Merged over defaults so a key added on the phone before the car
        // screen knows about it lands as a default rather than as undefined.
        indicators: { ...INDICATOR_DEFAULTS, ...(parsed.indicators ?? {}) },
        mapMode: parsed.mapMode ?? 'satellite',
        teeColour: parsed.teeColour ?? 'White',
        unit: parsed.unit ?? 'm',
        lockToHole: parsed.lockToHole ?? true,
        courseId: parsed.courseId ?? null,
      });
    } catch (err) {
      console.warn('[gocaddy] bad settings payload from the phone:', err);
    }
  };
  return () => { delete w.__gocaddyApplySettings; };
}
